/**
 * Reversibility classifier — surfaces "Cannot be undone" BEFORE approval.
 *
 * The reversal engine (reversal.ts) explains irreversibility *after* the
 * fact; this module makes it visible *before* the operator approves. Every
 * proposed action type is classified into one of three honest classes:
 *
 *   'full'          — the write has an exact inverse (field/status updates,
 *                     creates → delete). Undo restores the prior state.
 *   'compensating'  — the write can only be corrected, not exactly undone
 *                     (internal state changes like append-only activity
 *                     logs or snapshot-based recreate/delete). A governed
 *                     compensating proposal repairs it.
 *   'irreversible'  — the write leaves the building (email send, external
 *                     Slack post, GitHub PR merge). Nothing Argo does can
 *                     recall it, so approval requires an EXTRA confirm.
 *
 * Fail-safe by construction: an UNMAPPED action type classifies as
 * 'irreversible' — a new action kind added without a reversibility review
 * is gated hard rather than silently treated as safe.
 *
 * Pure logic only: no store, no firebase, no DOM.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Reversibility = 'full' | 'compensating' | 'irreversible';

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * Explicit class per known action type. Covers every native AgentAction
 * kind (src/lib/agent/types.ts), the two reversal-only kinds, and the
 * external side-effect kinds the ledger can carry (reversal.ts) plus the
 * external integrations (Slack post, GitHub PR merge).
 */
export const REVERSIBILITY_MAP: Record<string, Reversibility> = {
  // --- creates → compensating delete exists, treated as fully undoable ----
  create_person: 'full',
  create_document: 'full',
  create_ticket: 'full',
  create_task: 'full',

  // --- field / status updates → prior value is restorable -----------------
  set_prospect_stage: 'full',
  set_customer_status: 'full',
  move_workflow_stage: 'full',
  set_ticket_status: 'full',
  assign_ticket: 'full',
  schedule_follow_up: 'full',
  update_record_field: 'full',
  update_brain: 'full',

  // --- sequence autopilot (sequenceReplyEngine) ----------------------------
  // An auto-unenroll seals the prior enrollment list as its before-state, so
  // a compensating restore_enrollments proposal can put every enrollment back.
  auto_unenroll: 'full',
  restore_enrollments: 'compensating', // restores state; original timing drifts

  // --- internal state changes → correctable, not exactly undoable ---------
  log_activity: 'compensating', // append-only timeline; corrected, not erased
  delete_record: 'compensating', // recreated from snapshot, ids/refs may drift
  recreate_record: 'compensating',

  // --- left the building → cannot be recalled ------------------------------
  // The outbox doc can be cancelled while still queued, but once the send
  // function fires the email cannot be recalled — gate it hard.
  send_customer_update: 'irreversible',
  // Same outbox mechanics as send_customer_update: cancellable while queued,
  // unrecallable once the send function fires — classified by worst outcome.
  send_drafted_email: 'irreversible',
  // A live social post via the engine's Zernio adapter cannot be recalled.
  // (A dry run changes nothing, but the KIND is classified by its worst
  // honest outcome — fail-safe.)
  publish_content: 'irreversible',
  send_email: 'irreversible',
  external_send: 'irreversible',
  slack_post: 'irreversible',
  github_merge_pr: 'irreversible',
};

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

/**
 * Classify an action type. Unmapped types default to 'irreversible' —
 * fail-safe: a kind nobody reviewed is gated, never waved through.
 */
export function classifyReversibility(actionType: string): Reversibility {
  return REVERSIBILITY_MAP[actionType] ?? 'irreversible';
}

/**
 * Registry completeness check: given the action types a pipeline can emit,
 * report any that lack an explicit classification. Meant for tests / a
 * startup assertion so a new AgentAction kind cannot ship unreviewed.
 */
export function assertAllActionsClassified(actionTypes: string[]): {
  ok: boolean;
  unmapped: string[];
} {
  const unmapped = actionTypes.filter(
    (t) => !Object.prototype.hasOwnProperty.call(REVERSIBILITY_MAP, t),
  );
  return { ok: unmapped.length === 0, unmapped };
}

/** True iff approving this action must show the extra "Cannot be undone" confirm. */
export function requiresExtraConfirm(actionType: string): boolean {
  return classifyReversibility(actionType) === 'irreversible';
}

/**
 * Annotate a proposal with its reversibility class WITHOUT mutating the
 * original — returns a new object carrying a `reversibility` field.
 */
export function annotateProposal<T extends { actionType: string }>(
  p: T,
): T & { reversibility: Reversibility } {
  return { ...p, reversibility: classifyReversibility(p.actionType) };
}
